import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, ChevronLeft, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import StepCustomer from "./wizard/StepCustomer";
import StepProducts from "./wizard/StepProducts";
import StepDocuments from "./wizard/StepDocuments";
import StepSelfie from "./wizard/StepSelfie";
import StepTerms from "./wizard/StepTerms";
import StepPayment from "./wizard/StepPayment";
import StepConfirmation from "./wizard/StepConfirmation";

const ALL_STEPS = [
  { key: "customer", label: "Cliente", num: 1 },
  { key: "products", label: "Produtos", num: 2 },
  { key: "documents", label: "Documentos", num: 3 },
  { key: "selfie", label: "Selfie", num: 4 },
  { key: "terms", label: "Termos", num: 5 },
  { key: "payment", label: "Pagamento", num: 6 },
  { key: "confirmation", label: "Confirmação", num: 7 },
];

export default function SalesWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [customer, setCustomer] = useState(null);
  const [items, setItems] = useState([]);
  const [documents, setDocuments] = useState({ id_front: null, id_back: null });
  const [selfie, setSelfie] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("manual");
  const [notes, setNotes] = useState("");

  const requiresDocuments = items.some((i) => i.requires_document);
  const requiresSelfie = items.some((i) => i.requires_selfie);
  const requiresTerms = items.some((i) => i.requires_terms);

  const activeSteps = useMemo(() => {
    return ALL_STEPS.filter((s) => {
      if (s.key === "documents" && !requiresDocuments) return false;
      if (s.key === "selfie" && !requiresSelfie) return false;
      if (s.key === "terms" && !requiresTerms) return false;
      return true;
    });
  }, [requiresDocuments, requiresSelfie, requiresTerms]);

  const currentStepKey = activeSteps[currentStep]?.key;
  const isLastStep = currentStep === activeSteps.length - 1;
  const total = items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0);
  const monthlyTotal = items.reduce((sum, i) => sum + (i.monthly_price || 0) * i.quantity, 0);

  const canProceed = () => {
    switch (currentStepKey) {
      case "customer": return !!customer;
      case "products": return items.length > 0;
      case "documents": return !requiresDocuments || (documents.id_front && documents.id_back);
      case "selfie": return !requiresSelfie || !!selfie;
      case "terms": return !requiresTerms || termsAccepted;
      case "payment": return !!paymentMethod;
      case "confirmation": return true;
      default: return true;
    }
  };

  const handleNext = () => {
    if (!canProceed()) return;
    if (isLastStep) handleConfirm();
    else setCurrentStep((s) => Math.min(s + 1, activeSteps.length - 1));
  };

  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const handleConfirm = async () => {
    setSaving(true); setError("");
    try {
      await api.post("/sales", {
        customer_id: customer.id, customer_name: customer.name,
        items: items.map((i) => ({ product_id: i.product_id, product_name: i.product_name, quantity: i.quantity, unit_price: i.unit_price, monthly_price: i.monthly_price })),
        total, monthly_total: monthlyTotal, notes,
        documents: requiresDocuments ? documents : null,
        selfie: requiresSelfie ? selfie : null,
        terms_accepted: termsAccepted, payment_method: paymentMethod,
      });
      navigate("/crm/sales");
    } catch { setError("Erro ao salvar venda. Tente novamente."); }
    setSaving(false);
  };

  return (
    <div data-testid="sales-wizard">
      {/* Header */}
      <div className="mb-4">
        <h1 className="crm-heading flex items-center gap-2">
          <Sparkles className="w-6 h-6" style={{ color: "var(--crm-accent)" }} />
          Assistente de Venda
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--crm-text-muted)" }}>
          Passo {currentStep + 1} de {activeSteps.length}
        </p>
      </div>

      {/* Step indicator */}
      <div className="crm-card p-4 mb-8">
        <div className="flex items-center gap-0 overflow-x-auto">
          {activeSteps.map((step, idx) => {
            const isDone = idx < currentStep;
            const isActive = idx === currentStep;
            return (
              <div key={step.key} className="flex items-center flex-shrink-0">
                <div className="flex items-center gap-2.5 px-2 sm:px-3 py-1.5">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all"
                    style={{
                      background: isDone ? "var(--crm-accent)" : isActive ? "var(--crm-accent)" : "var(--crm-input-bg)",
                      color: isDone || isActive ? "white" : "var(--crm-text-dim)",
                      border: !isDone && !isActive ? "2px solid var(--crm-border)" : "none",
                      boxShadow: isActive ? "0 0 0 4px var(--crm-accent-subtle)" : "none",
                    }}
                  >
                    {isDone ? <Check className="w-4 h-4" /> : idx + 1}
                  </div>
                  <span
                    className="text-sm font-medium hidden sm:inline whitespace-nowrap"
                    style={{ color: isDone ? "var(--crm-accent)" : isActive ? "var(--crm-text)" : "var(--crm-text-dim)" }}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < activeSteps.length - 1 && (
                  <div className="w-6 sm:w-10 h-0.5 mx-1 flex-shrink-0 rounded-full transition-all"
                    style={{ background: idx < currentStep ? "var(--crm-accent)" : "var(--crm-border)" }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="min-h-[420px]">
        {currentStepKey === "customer" && <StepCustomer customer={customer} setCustomer={setCustomer} />}
        {currentStepKey === "products" && <StepProducts items={items} setItems={setItems} />}
        {currentStepKey === "documents" && <StepDocuments documents={documents} setDocuments={setDocuments} />}
        {currentStepKey === "selfie" && <StepSelfie selfie={selfie} setSelfie={setSelfie} />}
        {currentStepKey === "terms" && <StepTerms accepted={termsAccepted} setAccepted={setTermsAccepted} />}
        {currentStepKey === "payment" && <StepPayment method={paymentMethod} setMethod={setPaymentMethod} notes={notes} setNotes={setNotes} />}
        {currentStepKey === "confirmation" && <StepConfirmation customer={customer} items={items} total={total} monthlyTotal={monthlyTotal} documents={documents} selfie={selfie} termsAccepted={termsAccepted} paymentMethod={paymentMethod} notes={notes} />}
      </div>

      {error && (
        <div className="mt-6 p-4 rounded-xl text-sm font-medium" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444" }}>{error}</div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-10 pt-6" style={{ borderTop: "2px solid var(--crm-border)" }}>
        <button onClick={currentStep === 0 ? () => navigate("/crm/sales") : handleBack} className="crm-btn-secondary" data-testid="wizard-back-btn" style={{ padding: "12px 24px" }}>
          <ChevronLeft className="w-4 h-4" /> {currentStep === 0 ? "Cancelar" : "Voltar"}
        </button>
        <button onClick={handleNext} disabled={!canProceed() || saving} className="crm-btn-primary" data-testid="wizard-next-btn"
          style={{ padding: isLastStep ? "14px 32px" : "12px 24px", fontSize: isLastStep ? "15px" : "14px" }}>
          {saving ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Salvando...</>
          ) : isLastStep ? (
            <><Check className="w-5 h-5" /> Confirmar Venda</>
          ) : (
            <>Próximo <ChevronRight className="w-4 h-4" /></>
          )}
        </button>
      </div>
    </div>
  );
}
